import { z } from "zod";
const NonEmptyLine = z.string().trim().min(1).max(500);
export const ResponsibilitySchema = z.object({
    roleKey: z.string().trim().min(1).max(80),
    name: NonEmptyLine,
    owns: z.array(NonEmptyLine).max(40),
    mayApprove: z.array(NonEmptyLine).max(40),
    escalatesToRoleKey: z.string().trim().max(80).nullable()
});
export const WorkflowStageSchema = z.object({
    key: z.string().trim().min(1).max(80),
    label: NonEmptyLine,
    definitionOfDone: z.array(NonEmptyLine).min(1).max(30),
    nextStageKeys: z.array(z.string().trim().min(1).max(80)).max(20),
    requiredRoleKeys: z.array(z.string().trim().min(1).max(80)).max(20)
});
/**
 * A chat message may propose one of these rules, but it never activates it.
 * The approved Business Operating Model version remains the only authority.
 * `checkpointLeadMinutes` is deliberately narrow: it lets the cheap scheduler
 * calculate a pre-deadline checkpoint without asking a model on every scan;
 * policies without deadline timing keep it null and remain verified context.
 */
export const OperationalPolicySchema = z.object({
    key: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{2,79}$/),
    title: NonEmptyLine,
    appliesWhen: z.string().trim().min(1).max(500),
    requiredAction: z.string().trim().min(1).max(500),
    checkpointLeadMinutes: z.number().int().min(0).max(100_800).nullable(),
    evidenceSourceEventIds: z.array(z.uuid()).max(20).default([])
}).strict();
export const BusinessOperatingModelSchema = z.object({
    business: z.object({
        name: NonEmptyLine,
        mission: z.string().trim().min(1).max(2_000),
        services: z.array(NonEmptyLine).min(1).max(100),
        timezone: NonEmptyLine,
        businessHours: z.object({
            weekdays: z.array(z.number().int().min(0).max(6)).min(1),
            start: z.string().regex(/^\d{2}:\d{2}$/),
            end: z.string().regex(/^\d{2}:\d{2}$/)
        })
    }),
    responsibilities: z.array(ResponsibilitySchema).min(1).max(100),
    workflowStages: z.array(WorkflowStageSchema).min(1).max(100),
    vocabulary: z.array(z.object({
        term: NonEmptyLine,
        meaning: z.string().trim().min(1).max(1_000),
        aliases: z.array(NonEmptyLine).max(30)
    })).max(500),
    escalationRules: z.array(z.object({
        trigger: NonEmptyLine,
        afterMinutes: z.number().int().min(0).max(100_800),
        targetRoleKey: z.string().trim().min(1).max(80),
        maxAttempts: z.number().int().min(1).max(20)
    })).max(100),
    operationalPolicies: z.array(OperationalPolicySchema).max(100).default([]),
    factsFreshnessMinutes: z.record(z.string(), z.number().int().positive()).default({}),
    agentRole: z.object({
        purpose: z.string().trim().min(1).max(2_000),
        responsibilities: z.array(NonEmptyLine).min(1).max(50),
        prohibitedBehaviors: z.array(NonEmptyLine).min(1).max(50),
        answerStyle: z.object({
            maxLines: z.number().int().min(1).max(5),
            tone: NonEmptyLine,
            admitUnknown: z.string().trim().min(1).max(500)
        })
    })
});
export const BusinessOperatingModelVersionSchema = z.object({
    id: z.uuid(),
    workspaceId: z.uuid(),
    version: z.number().int().positive(),
    model: BusinessOperatingModelSchema,
    reason: z.string().trim().min(1).max(1_000),
    createdBy: z.uuid(),
    effectiveAt: z.iso.datetime(),
    createdAt: z.iso.datetime()
});
export const ContextManifestSchema = z.object({
    workspaceId: z.uuid(),
    operatingModelVersion: z.number().int().positive(),
    agentRoleVersion: z.number().int().positive(),
    workflowPackVersion: z.string().min(1),
    requesterId: z.uuid(),
    entityRefs: z.array(z.object({ type: z.string().min(1), id: z.uuid(), version: z.number().int().positive() })),
    factRefs: z.array(z.object({ id: z.uuid(), version: z.number().int().positive(), observedAt: z.iso.datetime() })),
    sourceEventIds: z.array(z.uuid()),
    builtAt: z.iso.datetime()
});
//# sourceMappingURL=business-model.js.map